import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Tpl3plService } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';

@Component({
	selector: 'app-tpl3pl-dashboard',
	templateUrl: './tpl3pl-dashboard.component.html',
	styleUrls: ['./tpl3pl-dashboard.component.scss'],
	animations: [
		trigger('throughputDetailEnter', [
			transition(':enter', [
				style({ opacity: 0, transform: 'perspective(900px) rotateX(-10deg)', transformOrigin: 'top center' }),
				animate(
					'260ms cubic-bezier(0.22, 1, 0.36, 1)',
					style({ opacity: 1, transform: 'perspective(900px) rotateX(0deg)' })
				)
			])
		])
	]
})
export class Tpl3plDashboardComponent implements OnInit, OnDestroy {
	private static readonly ERROR_PREVIEW_MAX = 120;
	private static readonly OI_SS_FROM = 'tpl3pl_dashboard_oi_from';
	private static readonly OI_SS_TO = 'tpl3pl_dashboard_oi_to';

	status: any = null;
	loading = false;
	oiSeriesFrom = '';
	oiSeriesTo = '';
	oiDetailMetric: null | 'orders' | 'exported' | 'invoiced' = null;
	rollAvgOrders = 0;
	rollTotalOrders = 0;
	rollAvgExported = 0;
	rollTotalExported = 0;
	rollAvgInvoiced = 0;
	rollTotalInvoiced = 0;
	private configSavedSub: Subscription | null = null;
	private oiRollRaf: number | null = null;

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.hydrateOiSeriesRange();
		this.loadStatus();
		this.configSavedSub = this.tpl3plService.configSaved$.subscribe(() => this.loadStatus());
	}

	ngOnDestroy(): void {
		this.configSavedSub?.unsubscribe();
		this.cancelOiRoll();
	}

	toggleOiDetail(which: 'orders' | 'exported' | 'invoiced'): void {
		this.oiDetailMetric = this.oiDetailMetric === which ? null : which;
	}

	closeThroughputDetail(): void {
		this.oiDetailMetric = null;
	}

	get throughputDetailTitle(): string {
		switch (this.oiDetailMetric) {
			case 'orders':
				return 'Orders by day';
			case 'exported':
				return 'Exported by day';
			case 'invoiced':
				return 'Invoiced by day';
			default:
				return '';
		}
	}

	private cancelOiRoll(): void {
		if (this.oiRollRaf != null) {
			cancelAnimationFrame(this.oiRollRaf);
			this.oiRollRaf = null;
		}
	}

	private animateOiRolls(m: {
		avgOrders: number;
		totalOrders: number;
		avgExported: number;
		totalExported: number;
		avgInvoiced: number;
		totalInvoiced: number;
	}): void {
		this.cancelOiRoll();
		const eps = 0.02;
		if (
			Math.abs(this.rollAvgOrders - m.avgOrders) < eps &&
			this.rollTotalOrders === m.totalOrders &&
			Math.abs(this.rollAvgExported - m.avgExported) < eps &&
			this.rollTotalExported === m.totalExported &&
			Math.abs(this.rollAvgInvoiced - m.avgInvoiced) < eps &&
			this.rollTotalInvoiced === m.totalInvoiced
		) {
			return;
		}
		const from = {
			avgOrders: this.rollAvgOrders,
			totalOrders: this.rollTotalOrders,
			avgExported: this.rollAvgExported,
			totalExported: this.rollTotalExported,
			avgInvoiced: this.rollAvgInvoiced,
			totalInvoiced: this.rollTotalInvoiced
		};
		const start = performance.now();
		const durationMs = 900;
		const ease = (t: number): number => 1 - Math.pow(1 - t, 3);
		const step = (now: number): void => {
			const raw = Math.min(1, (now - start) / durationMs);
			const t = ease(raw);
			this.rollAvgOrders = from.avgOrders + (m.avgOrders - from.avgOrders) * t;
			this.rollTotalOrders = Math.round(from.totalOrders + (m.totalOrders - from.totalOrders) * t);
			this.rollAvgExported = from.avgExported + (m.avgExported - from.avgExported) * t;
			this.rollTotalExported = Math.round(from.totalExported + (m.totalExported - from.totalExported) * t);
			this.rollAvgInvoiced = from.avgInvoiced + (m.avgInvoiced - from.avgInvoiced) * t;
			this.rollTotalInvoiced = Math.round(from.totalInvoiced + (m.totalInvoiced - from.totalInvoiced) * t);
			this.cdr.detectChanges();
			if (raw < 1) {
				this.oiRollRaf = requestAnimationFrame(step);
			} else {
				this.oiRollRaf = null;
				this.rollAvgOrders = m.avgOrders;
				this.rollTotalOrders = m.totalOrders;
				this.rollAvgExported = m.avgExported;
				this.rollTotalExported = m.totalExported;
				this.rollAvgInvoiced = m.avgInvoiced;
				this.rollTotalInvoiced = m.totalInvoiced;
				this.cdr.detectChanges();
			}
		};
		this.oiRollRaf = requestAnimationFrame(step);
	}

	private static toYmdLocal(d: Date): string {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private static isYmd(s: string): boolean {
		if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
			return false;
		}
		const [y, mo, da] = s.split('-').map((x) => parseInt(x, 10));
		const dt = new Date(y, mo - 1, da);
		return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === da;
	}

	private hydrateOiSeriesRange(): void {
		try {
			const f = sessionStorage.getItem(Tpl3plDashboardComponent.OI_SS_FROM);
			const t = sessionStorage.getItem(Tpl3plDashboardComponent.OI_SS_TO);
			if (
				f &&
				t &&
				Tpl3plDashboardComponent.isYmd(f) &&
				Tpl3plDashboardComponent.isYmd(t) &&
				f <= t
			) {
				this.oiSeriesFrom = f;
				this.oiSeriesTo = t;
				return;
			}
		} catch {
		}
		this.setMonthToDateRange(false);
	}

	private persistOiSeriesRange(): void {
		try {
			sessionStorage.setItem(Tpl3plDashboardComponent.OI_SS_FROM, this.oiSeriesFrom);
			sessionStorage.setItem(Tpl3plDashboardComponent.OI_SS_TO, this.oiSeriesTo);
		} catch {
		}
	}

	setMonthToDateRange(persist: boolean): void {
		const n = new Date();
		this.oiSeriesTo = Tpl3plDashboardComponent.toYmdLocal(n);
		this.oiSeriesFrom = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
		if (persist) {
			this.persistOiSeriesRange();
		}
	}

	applyOiSeriesRange(): void {
		if (!Tpl3plDashboardComponent.isYmd(this.oiSeriesFrom) || !Tpl3plDashboardComponent.isYmd(this.oiSeriesTo)) {
			this.globalSearchService.showNotification('Use a valid from and to date (YYYY-MM-DD).', 'warning', 'bottom', 'right');
			return;
		}
		if (this.oiSeriesFrom > this.oiSeriesTo) {
			const x = this.oiSeriesFrom;
			this.oiSeriesFrom = this.oiSeriesTo;
			this.oiSeriesTo = x;
		}
		this.persistOiSeriesRange();
		this.loadStatus();
	}

	monthToDateAndReload(): void {
		this.setMonthToDateRange(true);
		this.loadStatus();
	}

	previewFirstError(err: { message?: string } | null | undefined): string {
		const m = (err?.message ?? '').trim();
		const max = Tpl3plDashboardComponent.ERROR_PREVIEW_MAX;
		if (!m) {
			return '';
		}
		return m.length > max ? m.slice(0, max) + '…' : m;
	}

	loadStatus(): void {
		this.loading = true;
		const range =
			this.oiSeriesFrom && this.oiSeriesTo
				? { from: this.oiSeriesFrom, to: this.oiSeriesTo }
				: null;
		this.tpl3plService.getStatus(true, range).subscribe({
			next: (result) => {
				this.status = result;
				const m = result?.order_invoice_metrics;
				if (m?.series_date_from && m?.series_date_to) {
					this.oiSeriesFrom = m.series_date_from;
					this.oiSeriesTo = m.series_date_to;
					this.persistOiSeriesRange();
				}
				if (m) {
					this.animateOiRolls({
						avgOrders: Number(m.series_avg?.orders ?? 0),
						totalOrders: Number(m.series_totals?.orders ?? 0),
						avgExported: Number(m.series_avg?.exported ?? 0),
						totalExported: Number(m.series_totals?.exported ?? 0),
						avgInvoiced: Number(m.series_avg?.invoiced ?? 0),
						totalInvoiced: Number(m.series_totals?.invoiced ?? 0)
					});
				} else {
					this.cancelOiRoll();
					this.rollAvgOrders = 0;
					this.rollTotalOrders = 0;
					this.rollAvgExported = 0;
					this.rollTotalExported = 0;
					this.rollAvgInvoiced = 0;
					this.rollTotalInvoiced = 0;
				}
				this.loading = false;
			},
			error: () => {
				this.globalSearchService.showNotification('Error loading integration status', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	recoveryFromMinutes(mins: number | null | undefined): string {
		if (mins == null || !isFinite(mins) || mins <= 0) {
			return '—';
		}
		const h = mins / 60;
		if (h < 72) {
			return `${Math.round(h)} h`;
		}
		return `${(h / 24).toFixed(1)} d`;
	}
}
